/* ProjectScreen (DESIGN14 §F1.6) — one project as a native-feeling detail
   screen: a header with the project name + the four-stage PipelineRow, a
   compact stage rail and the stage bodies.

   The POSTER tab is owned here: an SVG preview inside a pinch-zoom / pan
   container (double-tap to fit), a "Sources" bottom Sheet, a prompt-edit
   bar pinned above the keyboard, generation via the Telegram MainButton (or
   the "⋯" menu once a poster exists), and the architecture-approve action.
   Generation is driven by the shared RunCard (resume-on-mount + Stop).

   DESIGN24 §T1 — THE MINI APP RUNS THE FULL CYCLE. Three things landed here:

     • SOURCES. The old single-field transcript drawer became the «Sources»
       sheet the web's left column already is: the call transcript, the
       requested-CASES planner (add / remove / reorder — the same wire field
       `phases`) and the extra instruction, committed together through the
       SAME PUT /inputs the web uses. The phone file picker also uses the
       existing upload, note and delete endpoints.

     • A FIRST ACTION THAT IS NEVER A 400. On an empty project the MainButton
       reads «Add sources» and opens that sheet; it only becomes «Generate
       architecture» once the project actually has source content (the server
       refuses a generate with nothing to read — the button must not walk the
       operator into that wall).

     • APPROVALS AS STAGE 1 DEFINED THEM. The architecture approve now speaks
       the policy engine: «Approved 1 of 2 · <time>», a quiet «You approved»
       state, and — for someone who is not an approver — a muted line naming
       who is expected instead of a button that would 403. With no policy
       configured every one of those is invisible and the screen behaves
       EXACTLY as it did before (the shared helpers in tmaApi enforce that).

   The ESTIMATE / PROPOSAL tabs are F2's screens, imported by the fixed props
   contract {projectId, architectureApproved, onPipelineChange}. This file
   never writes them — it only mounts them.

   Role logic is read the SAME way the web surface reads it: estimateAccess /
   proposalAccess off the shared useAuth() user + permissions. Every API call
   reuses the existing architectApi client (token attached by the global
   fetch interceptor) — no forked endpoints.

   DESIGN17 — FULL DOCUMENT COMPARTMENTALIZATION. The two document surfaces
   are mutually exclusive: the «Estimate» tab exists ONLY for canEstimate and
   the «Proposal» tab ONLY for canGenerate. A generator therefore sees
   Poster · Proposal, an estimator Poster · Estimate, root all three — and a
   tab that is not in the list is never mounted, so NO estimate endpoint is
   ever touched on a generator path (they all answer 403 now). The only
   estimate signal a generator gets is the pipeline strip stage, which is a
   status-only aggregate returned to every role; that is also why this screen
   keeps polling while the estimate is with the estimator. */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  architectApi,
  Phase,
  Pipeline,
  Run,
  pipelineHasHistoricalDocument,
  projectCodes,
} from '../../architect/api';
import { estimateAccess, pricingAccess } from '../../architect/api';
import { stageAccess } from '../../architect/stageAccess';
import { proposalAccess } from '../../architect/proposalApi';
import {
  Spec,
  VersionMeta,
  ProjectRecord,
  FileMeta,
} from '../../architect/types';
import {
  modelDisplay,
  fmtDate,
  swap,
  autoGrow,
} from '../../architect/utils';
import { useAuth } from '../../auth/AuthContext';
import {
  useBackButton,
  useMainButton,
  haptic,
  showConfirm,
} from '../telegram';
import {
  useApprovalStatus,
  canApproveNow,
  myApprovalTime,
} from '../tmaApi';
import { PinchZoom } from '../components/PinchZoom';
import { PosterViewer } from '../components/PosterViewer';
import { deliverFile } from '../files';
import { RunCard } from '../components/RunCard';
import { Sheet } from '../components/Sheet';
import { CommentsSheet } from '../components/CommentsSheet';
import { usePipelineResume } from '../../architect/usePipelineResume';
import { approvedStageGates } from '../../architect/WorkspaceStageNav';
import { StageHeader, StageFooter, StageAiButton } from '../../architect/StageChrome';
import { loadArchitectureSnapshotSvg } from '../../architect/architectureSnapshot';
import { architectureReviewIsStale, architectureReviewVersion, architectureReviewView } from '../../architect/architectureReview';
import ApproveAsSelector, { useApprovalActor } from '../../architect/ApproveAsSelector';
import { commentsApi, CommentStage } from '../../architect/api';
import { Download, Check, FolderOpen, MessageSquare, Loader2, Layers3, History, RotateCcw, Pencil, UserRound } from 'lucide-react';
import { useWorkspaceDraftGuard, useWorkspaceLeaveGuard } from '../../architect/useWorkspaceDraftGuard';
import { StructureSheet } from '../components/StructureSheet';
import { ArchitectureEditOptions, ArchitectureEditImpact } from '../../architect/ArchitectureEditMode';
import { ChatSheet, DocChatMessage, nextChatId } from '../components/DocSheet';
import type { CommentAnchor } from '../../architect/api';
import { anchorRevision, CommentPinRef } from '../../architect/CommentAnchors';

// F2's document tabs — imported by the FIXED props contract (DESIGN14 §F1.6).
// This file only mounts them; it never defines them.
import { EstimateTab } from './EstimateTab';
import EstimateApprovalGate, { estimateGateView, useEstimateApprovalGate } from '../../architect/EstimateApprovalGate';
import { PricingTab } from './PricingTab';
import SavedPricingHistory from '../../architect/SavedPricingHistory';
import { ProposalTab } from './ProposalTab';
import '../tma-workspace.css';
import '../tma-project-mobile.css';

interface Props {
  projectId: string;
  onBack: () => void;
  /** DESIGN40 — the queue opens a project straight on the step's tab. Falls
      back to the poster when that tab is not available to this account. */
  initialTab?: 'poster' | 'estimate' | 'pricing' | 'proposal';
}

type TabKey = 'poster' | 'estimate' | 'pricing' | 'proposal';

const STAGE_TABS: Array<{ key: TabKey; label: string }> = [
  { key: 'poster', label: 'Architecture' },
  { key: 'estimate', label: 'Estimate' },
  { key: 'pricing', label: 'Pricing' },
  { key: 'proposal', label: 'Proposal' },
];

function stageState(pipeline: Pipeline | undefined, stage: TabKey): 'done' | 'progress' | 'waiting' {
  if (stage === 'pricing') {
    const status = (pipeline as Pipeline & { pricing?: 'none' | 'draft' | 'approved' } | undefined)?.pricing;
    return status === 'approved' || (!status && pipeline?.priced) ? 'done' : status === 'draft' ? 'progress' : 'waiting';
  }
  const status = pipeline?.[stage === 'poster' ? 'architecture' : stage];
  return status === 'approved' ? 'done' : status === 'draft' || status === 'review' ? 'progress' : 'waiting';
}

function stageRunning(pipeline: Pipeline | undefined, stage: TabKey): boolean {
  return stage !== 'pricing' && !!pipeline?.running?.[stage === 'poster' ? 'architecture' : stage];
}

const POSTER_STEPS = [
  'Prepare sources',
  'AI designs the architecture',
  'Validate',
  'Render poster',
  'Save',
];

/* Input limits — mirrored from the backend validator (PUT /inputs) so a field
   can never be typed into a 400. */
const MAX_TRANSCRIPT = 300_000;
const MAX_EXTRA = 20_000;
const MAX_CASES = 12;
const MAX_CASE_TITLE = 120;
const MAX_CASE_DESC = 2_000;

/** A row of the requested-cases planner. UI says «CASE» (owner feedback #2:
    one project = one phase = one PDF holding several cases) while the WIRE
    field stays `phases` — same contract as the web sources column. */
interface CaseRow {
  title: string;
  description: string;
}

function toCaseRows(raw: unknown): CaseRow[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((p) => p && typeof p === 'object')
    .map((p) => ({
      title: typeof (p as Phase).title === 'string' ? String((p as Phase).title) : '',
      description:
        typeof (p as Phase).description === 'string' ? (p as Phase).description : '',
    }));
}

/** The wire payload: drop empty rows, trim, omit a blank title (the backend
    accepts `title: null`, and an empty string is noise in the prompt). */
function toPhasePayload(rows: CaseRow[]): Phase[] {
  return rows
    .filter((r) => r.description.trim())
    .map((r) => {
      const p: Phase = { description: r.description.trim() };
      if (r.title.trim()) p.title = r.title.trim();
      return p;
    });
}

function formatChars(n: number): string {
  // "12 340 chars" — thin-space thousands, same as the web sources column.
  return n.toLocaleString('en-US').replace(/,/g, ' ') + ' chars';
}

/* ───────────────────────── the poster tab ──────────────────────── */

const ARCHITECTURE_MODEL = 'claude-opus-4-8';
const ARCHITECTURE_CHAT_MODELS = [{ value: ARCHITECTURE_MODEL, label: 'AI' }];

interface PosterTabProps {
  projectId: string;
  spec: Spec | null;
  specFingerprint?: string | null;
  project: ProjectRecord | null;
  versions: VersionMeta[];
  /** The same uploaded sources used by the web architecture generator. */
  files: FileMeta[];
  /** May approve the architecture (estimate/root generate role). */
  canGenerate: boolean;
  canApprove?: boolean;
  isRoot?: boolean;
  requiresApprovalStatus?: boolean;
  /** The signed-in user's id — matches this viewer's own vote in the
      approvals list («You approved · <time>»). */
  userId: string | null;
  architectureApproved: boolean;
  onChanged: () => void | Promise<void>;
  /** Bumped by the parent after a rename so the SVG refetches even though the
      version count is unchanged (a rename re-renders the poster IN PLACE — no
      new version — so the versionCount-keyed reload below would miss it). */
  reloadKey: number;
  renameOpen: boolean;
  menuOpen: boolean;
  onMenuChange: (open: boolean) => void;
  onRename: () => void;
  onCommentAnchor?: (anchor: CommentAnchor) => void;
  pickingCommentPoint?: boolean;
  onCommentPointPicked?: () => void;
  jumpAnchor?: CommentAnchor | null;
  onJumpConsumed?: () => void;
  commentsOpen?: boolean;
}

export function PosterTab({
  projectId,
  spec,
  specFingerprint,
  project,
  versions,
  files,
  canGenerate,
  canApprove = canGenerate,
  isRoot = false,
  requiresApprovalStatus = false,
  userId,
  architectureApproved,
  onChanged,
  reloadKey,
  renameOpen,
  menuOpen,
  onMenuChange: setMenuOpen,
  onRename,
  onCommentAnchor,
  pickingCommentPoint,
  onCommentPointPicked,
  jumpAnchor,
  onJumpConsumed,
  commentsOpen,
}: PosterTabProps) {
  const versionCount = versions.length;
  const hasSpec = !!spec;
  const specRef = useRef(spec);
  specRef.current = spec;

  const [svgUrl, setSvgUrl] = useState<string | null>(null);
  const [svgRevision, setSvgRevision] = useState<string | null>(null);
  const [svgError, setSvgError] = useState(false);
  const [displayedArchitecture, setDisplayedArchitecture] = useState<{ projectId: string; versionId: string | null; reloadKey: number } | null>(null);
  const [svgReload, setSvgReload] = useState(0);
  const latestArchitectureVersionId = versions[0]?.id ?? null;
  const architectureSnapshotReady = hasSpec && !!displayedArchitecture && displayedArchitecture.projectId === projectId &&
    displayedArchitecture.versionId === latestArchitectureVersionId && displayedArchitecture.reloadKey === reloadKey;
  const [banner, setBanner] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const model = ARCHITECTURE_MODEL;
  const [pending, setPending] = useState<{
    run: Run;
    heading: string;
    excerpt?: string;
  } | null>(null);

  // ── the Sources sheet (transcript · cases · extra instruction) ──
  const [srcOpen, setSrcOpen] = useState(false);
  const [sourceError, setSourceError] = useState<string | null>(null);
  const [sourceUploadError, setSourceUploadError] = useState<string | null>(null);
  const [sourceFileError, setSourceFileError] = useState<string | null>(null);
  const [transcript, setTranscript] = useState('');
  const [extra, setExtra] = useState('');
  const [cases, setCases] = useState<CaseRow[]>([]);
  const [savingSrc, setSavingSrc] = useState(false);
  const [uploadingSrc, setUploadingSrc] = useState(false);
  const [fileNotes, setFileNotes] = useState<Record<string, string>>({});
  const [savingFileNote, setSavingFileNote] = useState<string | null>(null);
  const sourcePicker = useRef<HTMLInputElement>(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState<DocChatMessage[]>([]);
  const [chatDraft, setChatDraft] = useState({ dirty: false, busy: false });
  const [approving, setApproving] = useState(false);
  const [approvalActorOpen, setApprovalActorOpen] = useState(false);
  // DESIGN47 — the full-screen viewer + the file actions (signed links).
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerTargetPoint, setViewerTargetPoint] = useState<{ x: number; y: number } | null>(null);
  const [viewerTargetStale, setViewerTargetStale] = useState(false);
  const [structureOpen, setStructureOpen] = useState(false);
  const [structureTargetPath, setStructureTargetPath] = useState<string | null>(null);
  const [structureDraft, setStructureDraft] = useState({ dirty: false, busy: false });
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [restoring, setRestoring] = useState<string | null>(null);
  type StructuralEditOptions = { edit_mode: 'structural'; expected_version_id: string | null; expected_spec_fingerprint: string };
  const editBaseline = useRef<{ approved: boolean; versionId: string | null; fingerprint?: string | null; stale: boolean }>({ approved: architectureApproved, versionId: null, stale: true });
  const [structuralConfirmation, setStructuralConfirmation] = useState<{ title: string; confirmLabel: string } | null>(null);
  const structuralResolver = useRef<((accepted: boolean) => void) | null>(null);
  const promptEditOptions = useRef<StructuralEditOptions | null>(null);
  const finishStructuralConfirmation = useCallback((accepted: boolean) => {
    const resolve = structuralResolver.current;
    structuralResolver.current = null;
    setStructuralConfirmation(null);
    resolve?.(accepted);
  }, []);
  useEffect(() => () => { structuralResolver.current?.(false); structuralResolver.current = null; }, []);
  const requestStructuralChange = useCallback(async (title: string, confirmLabel: string): Promise<StructuralEditOptions | null | undefined> => {
    const baseline = editBaseline.current;
    if (!baseline.approved) return undefined;
    if (baseline.stale || !baseline.fingerprint) {
      throw new Error('The architecture changed. Reload it before making this edit.');
    }
    if (structuralResolver.current) return null;
    const options: StructuralEditOptions = { edit_mode: 'structural', expected_version_id: baseline.versionId, expected_spec_fingerprint: baseline.fingerprint };
    const accepted = await new Promise<boolean>(resolve => {
      structuralResolver.current = resolve;
      setStructuralConfirmation({ title, confirmLabel });
    });
    return accepted ? options : null;
  }, []);
  useWorkspaceDraftGuard(() => structureDraft, { onBusy: () => setBanner('Finish saving the architecture before leaving.') });
  const restoreVersion = useCallback(async (version: VersionMeta) => {
    if (!canGenerate || restoring) return;
    setHistoryError(null);
    try {
      const options = await requestStructuralChange(`Restore version ${version.seq}?`, 'Restore structure');
      if (options === null) return;
      if (!options && !await showConfirm(`Restore version ${version.seq}? The current architecture will remain in history.`)) return;
      setRestoring(version.id);
      if (options) await architectApi.restore(projectId, version.id, options);
      else await architectApi.restore(projectId, version.id);
      await onChanged();
      setHistoryOpen(false);
      setNote(`Version ${version.seq} restored.`);
      haptic.notify('success');
    } catch (error) {
      setHistoryError((error as Error).message || 'Could not restore this version.');
      haptic.notify('error');
    } finally {
      setRestoring(null);
    }
  }, [canGenerate, onChanged, projectId, requestStructuralChange, restoring]);
  const downloadPdf = useCallback(async () => {
    const how = await deliverFile('poster_pdf', projectId);
    setNote(how === 'telegram' ? 'Telegram is saving the PDF' : how === 'declined' ? 'Download cancelled' : 'The PDF opens in your browser');
  }, [projectId]);

  /* Seed the source editors ONCE per project. The parent polls GET /projects
     while a run is live (and while an estimate sits in review), so re-seeding
     on every `project` object would wipe whatever the operator is typing the
     moment a poll lands. `savedRef` doubles as the dirty baseline: it holds
     the last values the server is known to have. */
  const seededFor = useRef<string | null>(null);
  const savedRef = useRef<{ transcript: string; extra: string; cases: string }>({
    transcript: '',
    extra: '',
    cases: '[]',
  });
  useEffect(() => {
    if (!project || seededFor.current === projectId) return;
    seededFor.current = projectId;
    const t = ((project as any).main_transcript as string) || '';
    const e = ((project as any).extra_notes as string) || '';
    const rows = toCaseRows((project as any).phases);
    setTranscript(t);
    setExtra(e);
    setCases(rows);
    savedRef.current = {
      transcript: t,
      extra: e,
      cases: JSON.stringify(toPhasePayload(rows)),
    };
  }, [project, projectId]);

  // Load the SVG as an object URL so the token-bearing fetch works (an
  // <img src="/api/…"> could not attach the Authorization header). Reloads
  // Bind review eligibility to a loaded poster, including same-length version updates.
  useEffect(() => {
    setDisplayedArchitecture(null);
    setSvgUrl(null);
    if (!hasSpec) {
      setSvgRevision(null);
      setSvgError(false);
      return;
    }
    let cancelled = false;
    let url: string | null = null;
    setSvgError(false);
    loadArchitectureSnapshotSvg(projectId, specRef.current!, latestArchitectureVersionId)
      .then((txt) => {
        if (cancelled) return;
        url = URL.createObjectURL(new Blob([txt], { type: 'image/svg+xml' }));
        setSvgUrl(url);
        setDisplayedArchitecture({ projectId, versionId: latestArchitectureVersionId, reloadKey });
        setSvgRevision(anchorRevision({ spec: specRef.current, svg: anchorRevision(txt) }));
      })
      .catch(() => {
        if (!cancelled) setSvgError(true);
      });
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [projectId, hasSpec, latestArchitectureVersionId, versionCount, reloadKey, svgReload]);

  useEffect(() => {
    if (!jumpAnchor || jumpAnchor.document_id !== projectId || !spec || !svgUrl) return;
    if (jumpAnchor.kind === 'point' && Number.isFinite(jumpAnchor.x) && Number.isFinite(jumpAnchor.y)) {
      setViewerTargetPoint({ x: jumpAnchor.x!, y: jumpAnchor.y! });
      setViewerTargetStale(!!jumpAnchor.revision && jumpAnchor.revision !== svgRevision);
      setViewerOpen(true);
    } else if (jumpAnchor.kind === 'block' && jumpAnchor.path) {
      if (!architectureSnapshotReady) return;
      setStructureTargetPath(jumpAnchor.path);
      setStructureOpen(true);
    }
    onJumpConsumed?.();
  }, [architectureSnapshotReady, jumpAnchor, onJumpConsumed, projectId, spec, svgRevision, svgUrl]);

  const archDraft = hasSpec && !architectureApproved;

  /* DESIGN24 §T1.4 — the architecture step's approval progress, read from the
     SAME /api/approvals/status the web consumes. Fetched for every viewer who
     can see a poster (an estimator is allowed to read it and needs to know who
     is expected to approve); a failure leaves `status` null, which every
     helper reads as «no policy» = the pre-stage-1 behaviour. Polled slowly
     ONLY while a multi-approver step is still pending, so a colleague's vote
     in a «2 of 2» policy lands on its own without a permanent request loop. */
  const [multiApprover, setMultiApprover] = useState(false);
  const { status: approvals, reload: reloadApprovals } = useApprovalStatus(
    'architecture',
    'project',
    projectId,
    hasSpec,
    archDraft && multiApprover ? 20000 : 0,
  );
  const architectureReview = architectureReviewView(approvals);
  const approvalActor = useApprovalActor({ root: isRoot, status: approvals, step: 'architecture', targetId: projectId, enabled: archDraft });
  const approvalBody = approvalActor.body;
  const selectApprovalActor = approvalActor.select;
  const approvalAllowed = isRoot ? approvalActor.canApprove : canApproveNow(approvals) && !myApprovalTime(approvals, userId);
  const displayedArchitectureVersionId = displayedArchitecture?.projectId === projectId ? displayedArchitecture.versionId ?? undefined : undefined;
  const architectureReviewStale = hasSpec && (
    !architectureSnapshotReady ||
    architectureReviewIsStale(approvals, displayedArchitectureVersionId)
  );
  editBaseline.current = { approved: architectureApproved, versionId: displayedArchitecture?.versionId ?? null, fingerprint: specFingerprint, stale: !architectureSnapshotReady };
  useEffect(() => { reloadApprovals(); }, [reloadApprovals, displayedArchitectureVersionId]);
  useEffect(() => {
    setMultiApprover((approvals?.required ?? 1) > 1);
  }, [approvals]);

  /* A colleague's vote can be the one that meets the threshold. The parent
     only re-reads the project while a run is live, so the moment the counter
     says the step is satisfied we pull the header + pipeline forward
     ourselves — otherwise the strip would still read «draft» under a poster
     that is, in fact, approved. Fires once per crossing (the reload flips
     archDraft, which arms the guard again for the next draft). */
  const thresholdMet =
    !!approvals && approvals.required > 0 && approvals.given >= approvals.required;
  const syncedRef = useRef(false);
  useEffect(() => {
    if (!archDraft || !thresholdMet) {
      syncedRef.current = false;
      return;
    }
    if (syncedRef.current) return;
    syncedRef.current = true;
    setNote(null); // the «waiting for the others» line is spent
    onChanged();
  }, [archDraft, thresholdMet, onChanged]);

  const startGenerate = useCallback(async () => {
    if (!canGenerate) return;
    haptic.impact('medium');
    setBanner(null);
    try {
      const options = await requestStructuralChange('Regenerate architecture?', 'Regenerate structure');
      if (options === null) return;
      if (!options && hasSpec && !await showConfirm('Regenerate the architecture? The current version is kept in history.')) return;
      const { run } = await architectApi.generate(projectId, { model, ...options });
      setPending({ run, heading: 'Generating the architecture' });
    } catch (err) {
      haptic.notify('error');
      setBanner((err as Error).message || 'Could not start generation');
    }
  }, [canGenerate, projectId, model, hasSpec, requestStructuralChange]);

  /* Approve the architecture (DESIGN23 §B1 rules, mini-app chrome).
     With no policy the first vote flips the stage, exactly as before. With a
     «2 of 2» policy the same call records a vote and the response says «1 of
     2» — the stage stays draft and we say so instead of pretending it moved.
     A caller the policy excludes never gets here (the button is not shown),
     but a race — root narrows the policy while the sheet is open — is handled
     by surfacing the server's `not_an_approver` copy. */
  const approve = useCallback(async () => {
    if (!canApprove || !archDraft || (requiresApprovalStatus && !approvals) || !approvalAllowed || approving || busy || architectureReviewStale || structureDraft.dirty || structureDraft.busy) return;
    haptic.impact('medium');
    setBanner(null);
    setNote(null);
    setApproving(true);
    try {
      const res = approvals?.architecture_review || approvalBody.approve_as_user_id
        ? await architectApi.approveArchitecture(projectId, { ...architectureReviewVersion(approvals, displayedArchitectureVersionId), ...approvalBody })
        : await architectApi.approveArchitecture(projectId);
      selectApprovalActor('');
      haptic.notify('success');
      const given = res.approvals?.given;
      const need = res.approvals?.required;
      if (given != null && need != null && given < need) {
        setNote(
          architectureReview?.submitted || `Your approval is in — ${given} of ${need}. The architecture moves on once the others approve.`,
        );
      } else if (res.auto_started === 'estimate') {
        // DESIGN27 — the approval starts the estimate by itself; say so, and
        // the parent's reload flips the strip to the running dot right away.
        setNote('Approved — the estimate started automatically.');
      }
      reloadApprovals();
      onChanged();
    } catch (err) {
      haptic.notify('error');
      setBanner((err as Error).message || 'Could not approve');
      // A 403 / 409 means our picture of the step is stale — re-read it so
      // the button state matches the server on the next paint.
      reloadApprovals();
    } finally {
      setApproving(false);
    }
  }, [canApprove, archDraft, requiresApprovalStatus, approvalAllowed, approvalBody, selectApprovalActor, projectId, onChanged, approving, busy, reloadApprovals, approvals, displayedArchitectureVersionId, architectureReview?.submitted, architectureReviewStale, structureDraft]);

  const sendPrompt = useCallback(async (text: string) => {
    const instruction = text.trim();
    if (!canGenerate || !instruction || busy) return;
    haptic.impact('light');
    setBanner(null);
    const options = promptEditOptions.current;
    promptEditOptions.current = null;
    if (editBaseline.current.approved && !options) {
      setChatMessages(previous => previous.concat({ id: nextChatId(), role: 'error', text: 'Confirm structural changes before applying this AI edit.' }));
      return;
    }
    setChatMessages(previous => previous.concat({ id: nextChatId(), role: 'user', mode: 'edit', text: instruction }));
    try {
      const { run } = await architectApi.promptRun(projectId, {
        instruction,
        model,
        ...options,
      });
      setPending({
        run,
        heading: 'Applying your edit',
        excerpt:
          instruction.length > 80
            ? instruction.slice(0, 80).trimEnd() + '…'
            : instruction,
      });
    } catch (err) {
      haptic.notify('error');
      setBanner((err as Error).message || 'Could not apply the edit');
      setChatMessages(previous => previous.concat({ id: nextChatId(), role: 'error', text: (err as Error).message || 'Could not apply the edit' }));
    }
  }, [canGenerate, busy, projectId, model]);

  /* ── Sources: one PUT /inputs for the whole sheet ──────────────────
     The web column autosaves each field on a debounce; on a phone a sheet
     with an explicit «Save» (and an implicit save when it is dismissed) is
     the honest equivalent — one round trip, no half-written state, and the
     values survive in local state if the request fails so nothing is lost.
     `dirty` compares against the last values the server is known to hold. */
  const phasesJson = JSON.stringify(toPhasePayload(cases));
  const srcDirty =
    transcript !== savedRef.current.transcript ||
    extra !== savedRef.current.extra ||
    phasesJson !== savedRef.current.cases;
  const notesDirty = files.some(file => fileNotes[file.id] !== undefined && fileNotes[file.id] !== String(file.note || ''));
  useWorkspaceDraftGuard(() => ({
    dirty: srcDirty || notesDirty || chatDraft.dirty,
    busy: savingSrc || uploadingSrc || !!savingFileNote || chatDraft.busy,
  }), { onBusy: () => (srcOpen ? setSourceError : setBanner)('Wait for the sources to finish saving or uploading.') });

  const uploadSourceFiles = useCallback(async (selected: FileList | null) => {
    const chosen = Array.from(selected || []);
    if (!chosen.length) return;
    if (chosen.length > 10) { setSourceUploadError('Choose up to 10 source files at a time.'); return; }
    const tooLarge = chosen.find(file => file.size > 25 * 1024 * 1024);
    if (tooLarge) { setSourceUploadError(`“${tooLarge.name}” is over 25 MB.`); return; }
    setSourceUploadError(null);
    setUploadingSrc(true);
    try {
      const data = new FormData();
      chosen.forEach(file => data.append('files', file, file.name));
      await architectApi.uploadFiles(projectId, data);
      haptic.notify('success');
      await onChanged();
    } catch (error) {
      haptic.notify('error');
      setSourceUploadError((error as Error).message || 'Could not upload the source files.');
    } finally { setUploadingSrc(false); }
  }, [onChanged, projectId]);

  const saveFileNote = useCallback(async (file: FileMeta) => {
    const note = fileNotes[file.id];
    if (note === undefined || note === String(file.note || '') || savingFileNote) return;
    setSourceFileError(null);
    setSavingFileNote(file.id);
    try {
      await architectApi.patchFileNote(projectId, file.id, note);
      await onChanged();
    } catch (error) {
      haptic.notify('error');
      setSourceFileError((error as Error).message || 'Could not save the file note.');
    } finally { setSavingFileNote(null); }
  }, [fileNotes, onChanged, projectId, savingFileNote]);

  const removeSourceFile = useCallback(async (file: FileMeta) => {
    if (uploadingSrc || !(await showConfirm(`Remove “${file.filename}” from the sources?`))) return;
    setSourceFileError(null);
    setUploadingSrc(true);
    try {
      await architectApi.deleteFile(projectId, file.id);
      setFileNotes(previous => { const next = { ...previous }; delete next[file.id]; return next; });
      await onChanged();
      haptic.notify('success');
    } catch (error) {
      haptic.notify('error');
      setSourceFileError((error as Error).message || 'Could not remove the file.');
    } finally { setUploadingSrc(false); }
  }, [onChanged, projectId, uploadingSrc]);

  const saveSources = useCallback(
    async (close: boolean) => {
      if (savingSrc) return;
      const payload = {
        main_transcript: transcript,
        extra_notes: extra,
        phases: toPhasePayload(cases),
      };
      setSourceError(null);
      setSavingSrc(true);
      try {
        await architectApi.putInputs(projectId, payload);
        savedRef.current = {
          transcript,
          extra,
          cases: JSON.stringify(payload.phases),
        };
        haptic.notify('success');
        if (close) setSrcOpen(false);
        onChanged();
      } catch (err) {
        haptic.notify('error');
        setSourceError((err as Error).message || 'Could not save the sources');
      } finally {
        setSavingSrc(false);
      }
    },
    [projectId, transcript, extra, cases, savingSrc, onChanged],
  );

  // Dismissing the sheet (backdrop, grab handle, Escape) commits too — the
  // sheet is the editor, closing it is not a cancel.
  const closeSources = useCallback(async () => {
    if (savingSrc || uploadingSrc || savingFileNote) return;
    const pendingNotes = files.filter(file => fileNotes[file.id] !== undefined && fileNotes[file.id] !== String(file.note || ''));
    if (pendingNotes.length) {
      setSourceFileError(null);
      setSavingFileNote('all');
      try {
        for (const file of pendingNotes) await architectApi.patchFileNote(projectId, file.id, fileNotes[file.id]);
        setFileNotes(previous => { const next = { ...previous }; pendingNotes.forEach(file => { delete next[file.id]; }); return next; });
        await onChanged();
      } catch (error) {
        setSourceFileError((error as Error).message || 'Could not save the file notes.');
        setSavingFileNote(null);
        return;
      }
      setSavingFileNote(null);
    }
    if (srcDirty) await saveSources(true);
    else setSrcOpen(false);
  }, [files, fileNotes, onChanged, projectId, savingFileNote, savingSrc, srcDirty, saveSources, uploadingSrc]);

  const openSources = useCallback(() => {
    haptic.impact('light');
    setSourceError(null);
    setSourceUploadError(null);
    setSourceFileError(null);
    setSrcOpen(true);
  }, []);

  /* ── the cases planner (same wire field `phases` as the web) ── */
  const setCaseField = (i: number, key: keyof CaseRow, v: string) => {
    setCases((rows) => {
      const next = rows.slice();
      next[i] = { ...next[i], [key]: v };
      return next;
    });
  };
  const addCase = () => {
    haptic.impact('light');
    setCases((rows) =>
      rows.length >= MAX_CASES ? rows : rows.concat({ title: '', description: '' }),
    );
  };
  const removeCase = (i: number) => {
    haptic.impact('light');
    setCases((rows) => rows.filter((_, idx) => idx !== i));
  };
  const moveCase = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= cases.length) return;
    haptic.select();
    setCases((rows) => {
      const next = rows.slice();
      swap(next, i, j);
      return next;
    });
  };

  /* Does this project have anything to generate FROM? The server refuses a
     generate with no transcript, no extra instruction and no readable file
     (400 no_source_content), so a brand-new project's first action must be
     «Add sources» — never a button that walks into that refusal. Mirrors the
     backend's own check (routes.ts POST /generate). */
  const hasSources = !!transcript.trim() || !!extra.trim() || files.some(file =>
    /\.(txt|md|text|pdf|docx|csv|json)$/i.test(file.filename) && file.text_len !== 0);

  /* Who may press «Approve architecture» right now (DESIGN23 §C3 rules):
     the existing right (canGenerate), a draft to approve, the policy's own
     say (canApproveNow — TRUE when no policy is configured), and no vote of
     ours already on record. */
  const canApproveArch =
    canApprove && (!requiresApprovalStatus || !!approvals) && archDraft && approvalAllowed;


  // The shared stage footer owns the single approval action on every platform.
  useMainButton({ text: 'Approve architecture', visible: false, onClick: () => {} });

  return (
    <div className="tma-poster stage-mini-surface">
      <StageHeader title="Architecture" status={architectureApproved ? 'Approved' : busy ? 'Generating…' : hasSpec ? architectureReview?.badge || 'Draft' : 'Not started'} actions={[
        ...(canGenerate ? [{ id: 'sources', label: 'Sources', icon: <FolderOpen size={17}/>, onClick: openSources }] : []),
        ...(isRoot && canApprove && archDraft ? [{ id: 'approve-as', label: 'Approve as', icon: <UserRound size={17}/>, onClick: () => setApprovalActorOpen(true) }] : []),
        ...(hasSpec ? [
          ...(canGenerate ? [{ id: 'structure', label: architectureSnapshotReady ? 'Structure' : svgError ? 'Structure · reload required' : 'Structure · loading…', icon: <Layers3 size={17}/>, disabled: busy || !architectureSnapshotReady, onClick: () => { setStructureTargetPath(null); setStructureOpen(true); } }] : []),
          { id: 'versions', label: 'Version history', icon: <History size={17}/>, onClick: () => { setHistoryError(null); setHistoryOpen(true); } },
          { id: 'download', label: 'Download PDF', icon: <Download size={17}/>, disabled: busy, onClick: () => { void downloadPdf(); } },
        ] : []),
        { id: 'rename', label: 'Rename project', icon: <Pencil size={17}/>, onClick: onRename },
        ...(canGenerate ? [{ id: 'generate', label: hasSpec ? 'Regenerate architecture' : 'Generate architecture', icon: <RotateCcw size={17}/>, disabled: busy || !hasSources, onClick: () => { void startGenerate(); } }] : []),
      ]}/>
      {banner ? (
        <div className="tma-banner err" role="alert" onClick={() => setBanner(null)}>
          {banner}
        </div>
      ) : null}
      {note ? (
        <div className="tma-banner ok" role="status" onClick={() => setNote(null)}>
          {note}
        </div>
      ) : null}

      <RunCard<Run>
        projectId={projectId}
        run={pending?.run ?? null}
        poll={architectApi.getRun}
        canCancel={canGenerate}
        cancel={architectApi.cancelRun}
        getActive={architectApi.getActiveRun}
        heading={pending?.heading ?? 'Generating the architecture'}
        modelLabel={modelDisplay}
        excerpt={pending?.excerpt ?? null}
        steps={POSTER_STEPS}
        onDone={() => {
          setPending(null);
          setChatMessages(previous => previous.concat({ id: nextChatId(), role: 'assistant', text: 'Architecture updated. The new version is ready to review.' }));
          onChanged();
        }}
        onError={(msg) => {
          setPending(null);
          setBanner(msg);
          setChatMessages(previous => previous.concat({ id: nextChatId(), role: 'error', text: msg }));
        }}
        onBusyChange={setBusy}
      />

      {hasSpec ? (
        <>
          {svgError ? (
            <div className="tma-poster-empty">
              <div className="tma-empty-emoji">🖼️</div>
              <div className="tma-empty-title">Could not display the poster</div>
              <div className="tma-empty-sub">Pull down to refresh</div>
            </div>
          ) : svgUrl ? (
            <PinchZoom src={svgUrl} resetKey={versionCount} onTap={(point) => {
              haptic.select();
              if (pickingCommentPoint && point && spec) {
                onCommentAnchor?.({ kind: 'point', document_id: projectId, revision: svgRevision || anchorRevision(spec), label: 'Architecture · selected point', ...point });
                onCommentPointPicked?.();
              } else if (!pickingCommentPoint) setViewerOpen(true);
            }} />
          ) : (
            <div className="tma-poster-stage tma-poster-loading">
              <span className="tma-spin tma-poster-spin" />
            </div>
          )}
          <div className="tma-poster-hint">{pickingCommentPoint ? 'Tap a point on the poster to pin your comment.' : 'Tap to open full screen · pinch to zoom · double-tap to fit'}</div>
        </>
      ) : (
        <div className="tma-poster-empty">
          <div className="tma-empty-emoji">🧭</div>
          <div className="tma-empty-title">No architecture yet</div>
          <div className="tma-empty-sub">
            {hasSources
              ? 'The sources are in — generate the architecture when you’re ready.'
              : 'Start with the call transcript. The architecture is built from it.'}
          </div>
          {/* Held back until the project has actually loaded, so the label is
              never the wrong one for a frame. */}
          {project && canGenerate ? (
            <button
              type="button"
              className="tma-btn tma-btn-primary tma-empty-cta"
              onClick={hasSources ? startGenerate : openSources}
            >
              {hasSources ? 'Generate architecture' : 'Add sources'}
            </button>
          ) : null}
        </div>
      )}

      <>
        {canGenerate ? <StageAiButton mini open={chatOpen} disabled={!hasSpec} busy={busy} onClick={() => { haptic.select(); setChatOpen(value => !value); }}/> : null}
        <StageFooter className="stage-mini-footer" stage="architecture" pending={!architectureApproved} status={approvals} userId={userId} title={architectureApproved ? 'Architecture approved' : hasSpec ? architectureReview?.title || 'Ready for review' : 'Architecture not started'} hint={structureDraft.dirty ? 'Save before approving' : architectureReviewStale ? 'The architecture changed. Reload before reviewing.' : undefined}>
          {architectureReviewStale && !structureDraft.dirty ? <button type="button" className="arch-btn arch-btn-outline" disabled={busy || structureDraft.busy} onClick={() => { onChanged(); setSvgReload(value => value + 1); reloadApprovals(); }}>Reload architecture</button> : null}
          {canApproveArch ? <button type="button" className="arch-btn arch-btn-primary" disabled={approving || busy || architectureReviewStale || structureDraft.dirty || structureDraft.busy} onClick={() => { void approve(); }}>{approving ? 'Confirming…' : architectureReview?.action || 'Approve architecture'}</button> : null}
        </StageFooter>
      </>

      <StructureSheet open={canGenerate && structureOpen} onClose={() => { setStructureOpen(false); setStructureTargetPath(null); }}
        projectId={projectId} spec={spec} onChanged={onChanged}
        architectureApproved={architectureApproved} versionId={displayedArchitecture?.versionId} specFingerprint={specFingerprint} baselineLoaded={architectureSnapshotReady}
        onDraftStateChange={setStructureDraft} initialPath={structureTargetPath} />
      <Sheet open={isRoot && canApprove && archDraft && approvalActorOpen} onClose={() => setApprovalActorOpen(false)} title="Approve as"
        footer={<button type="button" className="tma-btn tma-btn-primary" onClick={() => setApprovalActorOpen(false)}>Done</button>}>
        <ApproveAsSelector actor={approvalActor} disabled={approving || busy || architectureReviewStale || structureDraft.dirty || structureDraft.busy}/>
      </Sheet>
      <Sheet open={historyOpen} onClose={() => { if (!restoring) setHistoryOpen(false); }} title="Architecture versions">
        {historyError ? <div className="tma-banner err" role="alert">{historyError}</div> : null}
        <div className="tma-version-intro">Restore an earlier design without losing the current one.</div>
        <div className="tma-version-list">
          {[...versions].sort((a, b) => b.seq - a.seq).map(version => <div key={version.id} className="tma-version-row">
            <span className="tma-version-number">v{version.seq}</span>
            <span className="tma-version-copy"><strong>{version.edit_mode === 'wording' ? 'Wording edit' : version.source === 'prompt' ? 'AI edit' : version.source === 'manual' ? 'Manual edit' : 'Architecture'}</strong><small>{fmtDate(version.created_at)}{version.model ? ` · ${modelDisplay(version.model)}` : ''}</small></span>
            <button type="button" className="tma-version-restore" disabled={!canGenerate || !!restoring || version.id === versions[0]?.id}
              onClick={() => void restoreVersion(version)} aria-label={`Restore version ${version.seq}`}>
              {restoring === version.id ? <Loader2 size={17} className="tma-stage-spin" /> : version.id === versions[0]?.id ? 'Current' : <RotateCcw size={17} />}
            </button>
          </div>)}
        </div>
      </Sheet>

      <ChatSheet key={projectId} open={canGenerate && chatOpen} onClose={() => setChatOpen(false)} title="Lato AI"
        models={ARCHITECTURE_CHAT_MODELS} model={model} onModelChange={() => {}} messages={chatMessages} busy={busy}
        beforeSend={async () => {
          const options = await requestStructuralChange('Apply an AI edit?', 'Apply structural edit');
          if (options === null) return false;
          promptEditOptions.current = options ?? null;
          return true;
        }}
        onSend={text => void sendPrompt(text)} onDraftStateChange={setChatDraft}
        editPrompts={['Rename the first case', 'Add a follow-up step']} />
      <Sheet open={!!structuralConfirmation} onClose={() => finishStructuralConfirmation(false)} title={structuralConfirmation?.title || 'Change architecture?'}
        className="tma-architecture-edit-confirm" footer={<div className="tma-architecture-edit-confirm-actions">
          <button type="button" className="tma-btn tma-btn-outline" onClick={() => finishStructuralConfirmation(false)}>Cancel</button>
          <button type="button" className="tma-btn tma-btn-primary" onClick={() => finishStructuralConfirmation(true)}>{structuralConfirmation?.confirmLabel}</button>
        </div>}>
        <ArchitectureEditOptions value="structural" onChange={() => {}} structuralOnly disabled />
        <ArchitectureEditImpact mode="structural" />
      </Sheet>

      {/* Sources keep transcript, uploaded files, cases and instruction in
          the same project as the web workspace. */}
      <Sheet
        open={canGenerate && srcOpen}
        onClose={closeSources}
        title="Sources"
        headerAction={
          <button
            type="button"
            className="tma-sheet-text-btn"
            disabled={savingSrc || uploadingSrc || !!savingFileNote || (!srcDirty && !notesDirty)}
            onClick={() => void closeSources()}
          >
            {savingSrc || savingFileNote ? 'Saving…' : srcDirty || notesDirty ? 'Save' : 'Saved'}
          </button>
        }
      >
        {sourceError ? <div className="tma-banner err" role="alert">{sourceError}</div> : null}
        <div className="tma-src-sec">
          <div className="tma-src-head">
            <span className="tma-src-title">Call transcript</span>
            <span className="tma-src-count">{formatChars(transcript.length)}</span>
          </div>
          <p className="tma-field-hint">
            Paste the transcript of your client call — the architecture is built
            from it.
          </p>
          <textarea
            className="tma-textarea tma-textarea-src"
            placeholder="— Tell me about your process…"
            maxLength={MAX_TRANSCRIPT}
            value={transcript}
            onChange={(e) => setTranscript(e.target.value)}
          />
        </div>

        <div className="tma-src-sec">
          <div className="tma-src-head">
            <span className="tma-src-title">Additional files</span>
            <span className="tma-src-count">{files.length} uploaded</span>
          </div>
          <p className="tma-field-hint">Documents and data files are read with your call notes. Up to 25 MB each.</p>
          {sourceUploadError ? <div className="tma-banner err" role="alert">{sourceUploadError}</div> : null}
          {sourceFileError ? <div className="tma-banner err" role="alert">{sourceFileError}</div> : null}
          <input ref={sourcePicker} className="tma-source-file-input" type="file" multiple
            accept=".txt,.md,.text,.pdf,.docx,.csv,.json"
            aria-label="Choose source files" onChange={event => {
              void uploadSourceFiles(event.currentTarget.files);
              event.currentTarget.value = '';
            }} />
          <button type="button" className="tma-source-upload" disabled={uploadingSrc}
            onClick={() => sourcePicker.current?.click()}>
            <span aria-hidden="true">{uploadingSrc ? '◌' : '↥'}</span>
            <strong>{uploadingSrc ? 'Uploading…' : 'Choose files'}</strong>
            <small>Up to 10 at once · TXT, MD, PDF, DOCX, CSV…</small>
          </button>
          {files.length ? <ul className="tma-source-files">{files.map(file => <li key={file.id} className="tma-source-file">
            <div className="tma-source-file-heading">
              <span className="tma-source-file-name" title={file.filename}>{file.filename}</span>
              <span className="tma-source-file-size">{file.size_bytes < 1024 ? `${file.size_bytes} B` : `${Math.round(file.size_bytes / 1024)} KB`}</span>
              <button type="button" className="tma-source-file-remove" aria-label={`Remove ${file.filename}`}
                disabled={uploadingSrc} onClick={() => void removeSourceFile(file)}>×</button>
            </div>
            <input className="tma-input tma-source-file-note" type="text" maxLength={1000}
              aria-label={`AI note for ${file.filename}`} placeholder="Note for the AI — what is this file?"
              value={fileNotes[file.id] ?? String(file.note || '')}
              onChange={event => setFileNotes(previous => ({ ...previous, [file.id]: event.target.value }))}
              onBlur={() => void saveFileNote(file)} />
          </li>)}</ul> : null}
        </div>

        <div className="tma-src-sec">
          <div className="tma-src-head">
            <span className="tma-src-title">Requested cases</span>
            <span className="tma-src-count">
              {cases.length} / {MAX_CASES}
            </span>
          </div>
          <p className="tma-field-hint">
            Optional. This project is one phase — a single document holding
            several cases. List the cases you want and what each covers.
          </p>
          {cases.map((c, i) => (
            <div className="tma-case" key={i}>
              <div className="tma-case-head">
                <span className="tma-case-chip">CASE {i + 1}</span>
                <div className="tma-case-ctrls">
                  <button
                    type="button"
                    className="tma-case-btn"
                    aria-label="Move case up"
                    disabled={i === 0}
                    onClick={() => moveCase(i, -1)}
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className="tma-case-btn"
                    aria-label="Move case down"
                    disabled={i === cases.length - 1}
                    onClick={() => moveCase(i, 1)}
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    className="tma-case-btn danger"
                    aria-label="Remove case"
                    onClick={() => removeCase(i)}
                  >
                    ✕
                  </button>
                </div>
              </div>
              <input
                className="tma-input tma-case-title"
                type="text"
                placeholder="Title (optional)"
                maxLength={MAX_CASE_TITLE}
                value={c.title}
                onChange={(e) => setCaseField(i, 'title', e.target.value)}
              />
              {/* Grows with its content — a case description that scrolls
                  inside four lines is unreadable on a phone. */}
              <textarea
                className="tma-textarea tma-case-desc"
                placeholder="What this case covers…"
                maxLength={MAX_CASE_DESC}
                value={c.description}
                ref={(el) => {
                  if (el) autoGrow(el);
                }}
                onChange={(e) => {
                  autoGrow(e.target as HTMLTextAreaElement);
                  setCaseField(i, 'description', e.target.value);
                }}
              />
            </div>
          ))}
          <button
            type="button"
            className="tma-add-row"
            disabled={cases.length >= MAX_CASES}
            onClick={addCase}
          >
            + Add case
          </button>
        </div>

        <div className="tma-src-sec">
          <div className="tma-src-head">
            <span className="tma-src-title">Extra instruction</span>
          </div>
          <p className="tma-field-hint">
            Global guidance for the AI — anything the transcript doesn’t say.
          </p>
          <textarea
            className="tma-textarea tma-textarea-extra"
            placeholder="e.g. keep the integrations lane separate…"
            maxLength={MAX_EXTRA}
            value={extra}
            onChange={(e) => setExtra(e.target.value)}
          />
        </div>
      </Sheet>

      <Sheet open={menuOpen} onClose={() => setMenuOpen(false)} title="Architecture actions">
        {isRoot && canApprove && archDraft ? <button type="button" className="tma-menu-row" onClick={() => { setMenuOpen(false); setApprovalActorOpen(true); }}>
          <span className="tma-menu-row-emoji"><UserRound size={20}/></span>
          <span className="tma-menu-row-body"><span className="tma-menu-row-title">Approve as</span></span>
        </button> : null}
        <button type="button" disabled={!canGenerate} className="tma-menu-row" onClick={() => { setMenuOpen(false); openSources(); }}>
          <span className="tma-menu-row-emoji"><FolderOpen size={20} /></span>
          <span className="tma-menu-row-body"><span className="tma-menu-row-title">Sources</span><span className="tma-menu-row-sub">Transcript, files and instructions</span></span>
        </button>
        {hasSpec ? <>
          <button type="button" disabled={!canGenerate || busy || !architectureSnapshotReady} className="tma-menu-row" onClick={() => { setMenuOpen(false); setStructureTargetPath(null); setStructureOpen(true); }}>
            <span className="tma-menu-row-emoji"><Layers3 size={20} /></span>
            <span className="tma-menu-row-body"><span className="tma-menu-row-title">Structure</span><span className="tma-menu-row-sub">{architectureSnapshotReady ? 'Review and edit architecture blocks' : svgError ? 'Reload the architecture to edit' : 'Loading architecture…'}</span></span>
          </button>
          <button type="button" className="tma-menu-row" onClick={() => { setMenuOpen(false); setHistoryError(null); setHistoryOpen(true); }}>
            <span className="tma-menu-row-emoji"><History size={20} /></span>
            <span className="tma-menu-row-body"><span className="tma-menu-row-title">Architecture versions</span><span className="tma-menu-row-sub">{versionCount} saved versions</span></span>
          </button>
        </> : null}
        <button type="button" className="tma-menu-row" onClick={() => { setMenuOpen(false); onRename(); }}>
          <span className="tma-menu-row-emoji"><Pencil size={20} /></span>
          <span className="tma-menu-row-body"><span className="tma-menu-row-title">Rename project</span></span>
        </button>
        {canGenerate ? <button type="button" className="tma-menu-row" disabled={busy || !hasSources}
          onClick={() => { setMenuOpen(false); void startGenerate(); }}>
          <span className="tma-menu-row-emoji"><RotateCcw size={20} /></span>
          <span className="tma-menu-row-body"><span className="tma-menu-row-title">{hasSpec ? 'Regenerate architecture' : 'Generate architecture'}</span><span className="tma-menu-row-sub">Build from the saved sources</span></span>
        </button> : null}
      </Sheet>

      <PosterViewer
        open={viewerOpen}
        src={svgUrl}
        title={project?.name || 'Architecture'}
        onClose={() => { setViewerOpen(false); setViewerTargetPoint(null); setViewerTargetStale(false); }}
        onDownloadPdf={downloadPdf}
        targetPoint={viewerTargetPoint}
        targetFromOlderVersion={viewerTargetStale}
      />
    </div>
  );
}

/* ─────────────────────────── the screen ─────────────────────────── */

export default function ProjectScreen({ projectId, onBack, initialTab }: Props) {
  const { user, permissions } = useAuth();
  const eAccess = useMemo(
    () => estimateAccess(user, permissions),
    [user, permissions],
  );
  const canPricing = pricingAccess(user, permissions);
  const pAccess = useMemo(
    () => proposalAccess(user, permissions),
    [user, permissions],
  );

  const [project, setProject] = useState<ProjectRecord | null>(null);
  const [spec, setSpec] = useState<Spec | null>(null);
  const [specFingerprint, setSpecFingerprint] = useState<string | null>(null);
  const [pipeline, setPipeline] = useState<Pipeline | undefined>(undefined);
  const [versions, setVersions] = useState<VersionMeta[]>([]);
  /* The same source files are available in both the web and Mini App. */
  const [files, setFiles] = useState<FileMeta[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [tab, setTab] = useState<TabKey>(initialTab ?? (
    eAccess.canGenerate ? 'poster'
    : eAccess.canEstimate ? 'estimate'
    : canPricing ? 'pricing'
    : pAccess.canSee ? 'proposal'
    : 'poster'
  ));
  const [historyMenuOpen, setHistoryMenuOpen] = useState(false);
  const [recoveringWorkflow, setRecoveringWorkflow] = useState(false);
  const [proposalNonce, setProposalNonce] = useState(0);
  // DESIGN43 — the stage's comment thread, one sheet per tab. Counts are
  // decoration and never block the screen.
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [commentsDraft, setCommentsDraft] = useState({ dirty: false, busy: false });
  const [pricingDraft, setPricingDraft] = useState({ dirty: false, busy: false });
  const [commentAnchor, setCommentAnchor] = useState<CommentAnchor | null>(null);
  const [focusedThreadId, setFocusedThreadId] = useState<string | null>(null);
  const [pickingCommentPoint, setPickingCommentPoint] = useState(false);
  const [pickingCommentBlock, setPickingCommentBlock] = useState(false);
  const [jumpAnchor, setJumpAnchor] = useState<CommentAnchor | null>(null);
  const [commentCounts, setCommentCounts] = useState<Partial<Record<CommentStage, number>>>({});
  const [commentPins, setCommentPins] = useState<CommentPinRef[]>([]);
  const pinIndexRequest = useRef(0);
  const loadCommentCounts = useCallback(async () => {
    try {
      const r = await commentsApi.counts(projectId);
      setCommentCounts(r.counts || {});
    } catch {
      /* decoration */
    }
  }, [projectId]);
  useEffect(() => {
    void loadCommentCounts();
  }, [loadCommentCounts]);

  // Rename (DESIGN18 §F2) — a bottom sheet with the Save on the Telegram
  // MainButton (borrowed from the poster tab; see PosterTab.renameMb).
  const [renameOpen, setRenameOpen] = useState(false);
  const [architectureMenuOpen, setArchitectureMenuOpen] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [renaming, setRenaming] = useState(false);
  const [renameError, setRenameError] = useState<string | null>(null);
  // Best-effort cascade warnings (a document that failed to re-render) — the
  // rename itself still landed. Shown as a dismissible note, never an error.
  const [notice, setNotice] = useState<string | null>(null);
  useWorkspaceDraftGuard(() => ({
    dirty: pricingDraft.dirty || commentsDraft.dirty,
    busy: pricingDraft.busy || commentsDraft.busy,
  }), { onBusy: () => setNotice('Finish the current comment or recording before leaving.') });
  const requestLeave = useWorkspaceLeaveGuard();
  // Bumped on a successful rename so PosterTab refetches the re-rendered SVG
  // (a rename keeps the same version, so the version-count reload would miss).
  const [posterNonce, setPosterNonce] = useState(0);

  const load = useCallback(async () => {
    try {
      const d = await architectApi.getProject(projectId);
      setProject(d.project);
      setSpec(d.spec ?? null);
      setSpecFingerprint(d.spec_fingerprint ?? null);
      setPipeline(d.pipeline);
      setVersions(d.versions || []);
      setFiles(d.files || []);
      setLoadError(null);
    } catch (err) {
      setLoadError((err as Error).message || 'Could not load the project');
    } finally {
      setLoaded(true);
    }
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  // Soft-poll the header pipeline while any module is running so the dots
  // clear themselves. The version count only changes on a completed run, so
  // the poster SVG never reloads mid-run.
  //
  // DESIGN17: a generator has no estimate surface at all, so the strip is
  // their ONLY window on the estimator's work — keep a slow poll going while
  // an estimate sits in review so the stage flips to ✓ (and the server-side
  // proposal gate opens) without a manual reload.
  const running = pipeline?.running;
  const anyRunning = !!(
    running &&
    (running.architecture || running.estimate || running.proposal)
  );
  const awaitingAutomaticHandoff =
    (pipeline?.architecture === 'approved' && pipeline.estimate === 'none') ||
    (pipeline?.pricing === 'approved' && pipeline.proposal === 'none');
  const awaitingReview = pipeline?.architecture === 'draft' || pipeline?.estimate === 'review' ||
    pipeline?.pricing === 'draft' || pipeline?.proposal === 'review';
  const pollMs = anyRunning ? 5000 : awaitingAutomaticHandoff || awaitingReview ? 20000 : 0;
  useEffect(() => {
    if (!pollMs) return undefined;
    const t = window.setInterval(() => void load(), pollMs);
    return () => window.clearInterval(t);
  }, [pollMs, load]);

  const architectureApproved =
    (pipeline?.architecture ??
      ((project as any)?.architecture_status as string | undefined)) ===
    'approved';
  const estimateApprovalStatus = useEstimateApprovalGate(projectId, pipeline, eAccess.canViewEstimate);
  const estimateGate = estimateGateView(pipeline, estimateApprovalStatus, user?.id);
  const staleEstimate = architectureApproved && pipeline?.estimate === 'none' && !!pipeline.historical?.estimate;
  const approvalGates = approvedStageGates(pipeline || null);
  const stageUnlocked = (key: TabKey) => approvalGates[key === 'poster' ? 'architecture' : key];

  // Read-only document code chips (DESIGN20 §F2) — W/TS for the architecture
  // (or tech-scope) doc, plus a muted LP once a proposal exists. Same computed
  // rule as the web surface (one source). Both null until the project loads /
  // gets a number, in which case the header renders nothing.
  const codes = projectCodes(
    (project as any)?.number,
    (project as any)?.doc_kind,
    pipeline,
  );

  // DESIGN17 — the two document tabs are mutually exclusive by role: Estimate
  // for canEstimate only, Proposal for canGenerate only (root/dev hold both).
  // Poster is always there, so the body is never empty.
  const roleCanOpen = (key: TabKey) => (key === 'poster' && eAccess.canViewArchitecture) ||
    (key === 'estimate' && eAccess.canViewEstimate) ||
    (key === 'pricing' && canPricing) ||
    (key === 'proposal' && pAccess.canSee);
  const hasLegacyDocument = (key: 'estimate' | 'pricing' | 'proposal') =>
    roleCanOpen(key) && (!stageUnlocked(key) || (key === 'estimate' && staleEstimate)) && pipelineHasHistoricalDocument(pipeline, key);
  // Saved artifacts remain readable independently of the current review round.
  // A history tab never mounts the editing workspace behind its approval gate.
  const tabs = STAGE_TABS.filter(({ key }) => roleCanOpen(key) &&
    (stageUnlocked(key) || (key !== 'poster' && hasLegacyDocument(key))));
  // Guard: a role change (or a first mount on a stale key) must never strand
  // us on a tab this account cannot see — fall back to Poster.
  const tabAvailable = tabs.some((t) => t.key === tab);
  const activeTab: TabKey | null = tabAvailable ? tab : tabs[0]?.key ?? null;
  const legacyView = activeTab != null && activeTab !== 'poster' && hasLegacyDocument(activeTab);
  const historyExitStage = STAGE_TABS.find(({ key }) => roleCanOpen(key) && stageUnlocked(key)
    && (key === 'poster' || !hasLegacyDocument(key)))?.key;
  const pipelineResume = usePipelineResume({
    projectId, pipeline,
    revision: project?.architecture_approved_at || '',
    enabled: loaded && !legacyView && !recoveringWorkflow && (eAccess.canGenerate || eAccess.canEstimate),
    onRefresh: async current => { setPipeline(current); await load(); },
  });
  useEffect(() => { if (pipelineResume.error) setNotice(pipelineResume.error); }, [pipelineResume.error]);
  const commentStage: CommentStage | null =
    activeTab === 'poster' ? (eAccess.canGenerate || (user?.stage_access && eAccess.canViewArchitecture) ? 'architecture' : null)
    : activeTab === 'estimate' ? 'estimate'
    : activeTab === 'pricing' ? 'pricing'
    : activeTab === 'proposal' ? 'proposal'
    : null;
  const commentRights = commentStage ? stageAccess(user, permissions, commentStage) : null;
  const canWriteComments = !!commentRights?.view && (!commentRights.configured || commentRights.edit || commentRights.approve);
  useEffect(() => {
    if (canWriteComments) return;
    setPickingCommentPoint(false);
    setPickingCommentBlock(false);
    setCommentAnchor(null);
  }, [canWriteComments]);
  const loadCommentPins = useCallback(async () => {
    const request = ++pinIndexRequest.current;
    if (!commentStage || legacyView) { setCommentPins([]); return; }
    setCommentPins([]);
    const pins: CommentPinRef[] = [];
    const seen = new Set<string>();
    let cursor: string | undefined;
    try {
      do {
        const page = await commentsApi.anchors(projectId, commentStage, cursor);
        if (request !== pinIndexRequest.current) return;
        pins.push(...page.anchors.filter(row => !!row.anchor).map(row => ({ id: row.id, anchor: row.anchor! })));
        cursor = page.next_cursor || undefined;
        if (cursor && seen.has(cursor)) break;
        if (cursor) seen.add(cursor);
      } while (cursor);
      if (request === pinIndexRequest.current) setCommentPins(pins);
    } catch { /* The stage and its general conversation remain usable offline. */ }
  }, [projectId, commentStage, legacyView]);
  useEffect(() => {
    void loadCommentPins();
    return () => { pinIndexRequest.current += 1; };
  }, [loadCommentPins]);
  useEffect(() => {
    if (loaded && !tabAvailable) {
      setTab('poster');
    }
  }, [loaded, tabAvailable]);

  const selectStage = useCallback((next: TabKey) => {
    if (activeTab === next || !tabs.some(item => item.key === next)) return;
    requestLeave(() => {
      haptic.select();
      setCommentsOpen(false);
      setHistoryMenuOpen(false);
      setCommentAnchor(null);
      setFocusedThreadId(null);
      setPickingCommentPoint(false);
      setPickingCommentBlock(false);
      setJumpAnchor(null);
      setTab(next);
    });
  }, [activeTab, tabs, requestLeave]);

  const openLegacyHistory = (next: 'estimate' | 'pricing' | 'proposal') => {
    if (!hasLegacyDocument(next)) return;
    requestLeave(() => {
      haptic.select();
      setCommentsOpen(false);
      setHistoryMenuOpen(false);
      setCommentAnchor(null);
      setJumpAnchor(null);
      setTab(next);
    });
  };

  const closeLegacyHistory = () => {
    if (!historyExitStage) return;
    requestLeave(() => {
      setTab(historyExitStage);
    });
  };

  useBackButton(true, onBack);

  const openCommentAnchor = useCallback((anchor: CommentAnchor) => {
    if (!canWriteComments) return;
    setCommentAnchor(anchor);
    setFocusedThreadId(null);
    setPickingCommentPoint(false);
    setPickingCommentBlock(false);
    setCommentsOpen(true);
  }, [canWriteComments]);
  const openCommentThread = useCallback((id: string) => {
    setCommentAnchor(null);
    setFocusedThreadId(id);
    setPickingCommentPoint(false);
    setPickingCommentBlock(false);
    setCommentsOpen(true);
  }, []);

  const openRename = useCallback(() => {
    haptic.impact('light');
    setRenameValue(project?.name || '');
    setRenameError(null);
    setRenameOpen(true);
  }, [project]);

  const doRename = useCallback(async () => {
    if (!project) return;
    const trimmed = renameValue.trim();
    if (!trimmed || trimmed === project.name || renaming) return;
    haptic.impact('medium');
    setRenaming(true);
    setRenameError(null);
    try {
      const res = await architectApi.renameProject(projectId, trimmed);
      haptic.notify('success');
      setRenameOpen(false);
      setNotice(res.warnings && res.warnings.length ? res.warnings.join(' · ') : null);
      // Force the re-rendered poster SVG to refetch, then refresh the header
      // name + pipeline (a rename never touches architecture/estimate/proposal
      // status, so the pipeline strip is unchanged — this just re-reads it).
      setPosterNonce((n) => n + 1);
      await load();
    } catch (err) {
      haptic.notify('error');
      setRenameError((err as Error).message || 'Could not rename the project');
    } finally {
      setRenaming(false);
    }
  }, [project, renameValue, renaming, projectId, load]);

  // Rename saves inside its sheet so the native footer remains reserved for
  // a stage approval.
  const renameTrimmed = renameValue.trim();
  const renameCanSave =
    !!project && renameTrimmed.length > 0 && renameTrimmed !== project.name;

  return (
    <div className="tma-screen tma-project tma-workspace">
      <div className="tma-project-head">
        {!loaded ? (
          <div className="tma-skel tma-skel-name" style={{ width: '60%' }} />
        ) : (
          <div className="tma-project-identity">
            <span className="tma-project-identity-icon" aria-hidden="true"><FolderOpen size={19} strokeWidth={1.8} /></span>
            <div className="tma-project-identity-copy">
              <div className="tma-project-name-row"><h1 className="tma-project-name">{project?.name || 'Project'}</h1>
              </div>
              <div className="tma-project-identity-sub">
                <span>{project?.client_name ? `${project.client_name} · ${project.phase_title || 'Phase'}` : 'Project workspace'}</span>
                {codes.docCode ? <span className="tma-code-chip">{codes.docCode}</span> : null}
                {codes.proposalCode ? <span className="tma-code-chip muted">{codes.proposalCode}</span> : null}
              </div>
            </div>
            {commentStage && !legacyView && (activeTab !== 'poster' || eAccess.canGenerate || !!user?.stage_access) ? <button type="button" className="tma-project-comment-action" aria-label={`Comments on ${activeTab === 'poster' ? 'architecture' : activeTab}`} onClick={() => { haptic.impact('light'); setCommentAnchor(null); setFocusedThreadId(null); setCommentsOpen(true); }}><MessageSquare size={18} strokeWidth={1.8} />{commentCounts[commentStage] ? <span className="tma-project-comment-count">{commentCounts[commentStage]}</span> : null}</button> : null}
          </div>
        )}
        <div className="tma-workspace-stages" role="tablist" aria-label="Project stages">
          {STAGE_TABS.map(({ key, label }, index) => {
            const accessible = tabs.some(item => item.key === key);
            const archived = key !== 'poster' && hasLegacyDocument(key) && !stageUnlocked(key);
            const roleAllowed = roleCanOpen(key);
            const state = stageState(pipeline, key);
            const running = stageRunning(pipeline, key);
            const lockedReason = key === 'pricing' && estimateGate?.reason ? estimateGate.reason : 'locked until the previous stage is approved';
            return <button key={key} type="button" role="tab" aria-selected={activeTab === key} aria-disabled={!accessible} disabled={!accessible}
              aria-label={`${label}, ${archived ? 'saved history, read only' : running ? 'generating' : state === 'done' ? 'approved' : state === 'progress' ? 'in review' : 'not started'}${accessible ? '' : roleAllowed ? `, ${lockedReason}` : ', unavailable for your role'}`}
              title={!accessible && roleAllowed ? lockedReason : undefined}
              className={'tma-workspace-stage' + (activeTab === key ? (legacyView ? ' archived' : ' active') : '') + ' ' + state}
              onClick={() => selectStage(key)}>
              <span className="tma-workspace-stage-icon" aria-hidden="true">{archived ? <History size={13} /> : running ? <Loader2 size={13} className="tma-stage-spin" /> : state === 'done' ? <Check size={12} strokeWidth={2.5} /> : state === 'progress' ? <span className="tma-stage-progress-dot" /> : index + 1}</span>
              <span className="tma-workspace-stage-label">{label}</span>
              <span className="tma-workspace-stage-short" aria-hidden="true">{key === 'poster' ? 'Arch' : key === 'estimate' ? 'Estimate' : key === 'pricing' ? 'Pricing' : 'Proposal'}</span>
            </button>;
          })}
        </div>
      </div>

      {commentStage && !legacyView && (activeTab !== 'poster' || eAccess.canGenerate || !!user?.stage_access) ? (
        <CommentsSheet
          open={commentsOpen}
          onClose={() => setCommentsOpen(false)}
          projectId={projectId}
          stage={commentStage}
          onChanged={() => { void loadCommentCounts(); void loadCommentPins(); }}
          onDraftStateChange={setCommentsDraft}
          anchor={commentAnchor}
          focusedThreadId={focusedThreadId}
          onClearAnchor={() => setCommentAnchor(null)}
          onPickAnchor={!canWriteComments || (activeTab === 'poster' && !spec) ? undefined : () => {
            setCommentsOpen(false);
            setPickingCommentPoint(activeTab === 'poster');
            setPickingCommentBlock(activeTab === 'estimate' || activeTab === 'pricing' || activeTab === 'proposal');
          }}
          onJumpToAnchor={anchor => { setCommentsOpen(false); setJumpAnchor(anchor); }}
        />
      ) : null}

      {loadError ? (
        <div className="tma-banner err" role="alert">
          {loadError}
        </div>
      ) : null}
      {notice ? (
        <div
          className="tma-banner warn"
          role="status"
          onClick={() => setNotice(null)}
        >
          {notice}
        </div>
      ) : null}

      <div className="tma-project-body">
        {!activeTab && loaded ? <p className="tma-doc-notice">No available stages. Your stage access or an earlier approval is required.</p> : null}
        {activeTab === 'estimate' && !legacyView && estimateGate?.locked ? <EstimateApprovalGate view={estimateGate} variant="tma" /> : null}
        {legacyView ? <div className="tma-workspace-history-view" role="note">
          <span>Saved {activeTab} · read only</span>
          {historyExitStage ? <button type="button" onClick={closeLegacyHistory}>Close history</button> : null}
        </div> : hasLegacyDocument('estimate') || hasLegacyDocument('pricing') || hasLegacyDocument('proposal') ? (
          <div className="tma-workspace-history-shortcut">
            <button type="button" onClick={() => setHistoryMenuOpen(true)}><History size={16} aria-hidden="true" /> Document history</button>
          </div>
        ) : null}
        {canWriteComments && pickingCommentBlock ? <div className="tma-comment-pick-notice" role="status">
          {activeTab === 'pricing' ? 'Tap the marker beside a price row to discuss it.' : 'Tap the section you want to discuss.'}
          <button type="button" onClick={() => setPickingCommentBlock(false)}>Cancel</button>
        </div> : null}
        {activeTab === 'poster' ? (
          <PosterTab
            projectId={projectId}
            spec={spec}
            specFingerprint={specFingerprint}
            project={project}
            versions={versions}
            files={files}
            canGenerate={eAccess.canGenerate}
            canApprove={eAccess.canApproveArchitecture}
            isRoot={!!user?.isRoot}
            requiresApprovalStatus={!!user?.stage_access?.architecture?.configured}
            userId={user?.id ?? null}
            architectureApproved={architectureApproved}
            onChanged={load}
            reloadKey={posterNonce}
            renameOpen={renameOpen}
            menuOpen={architectureMenuOpen}
            onMenuChange={setArchitectureMenuOpen}
            onRename={openRename}
            onCommentAnchor={canWriteComments ? openCommentAnchor : undefined}
            pickingCommentPoint={canWriteComments && pickingCommentPoint}
            onCommentPointPicked={() => setPickingCommentPoint(false)}
            jumpAnchor={jumpAnchor}
            onJumpConsumed={() => setJumpAnchor(null)}
            commentsOpen={commentsOpen || historyMenuOpen}
          />
        ) : null}

        {/* Mounted ONLY for an estimator (or root/dev): the estimate routes
            answer 403 to everyone else, so a generator must never mount this
            tab — it would fire a forbidden list call on open. */}
        {activeTab === 'estimate' && eAccess.canViewEstimate ? (
          <EstimateTab
            projectId={projectId}
            readOnly={legacyView || staleEstimate}
            architectureApproved={architectureApproved}
            onPipelineChange={load}
            onCommentAnchor={!legacyView && canWriteComments ? openCommentAnchor : undefined}
            commentPins={commentPins}
            onOpenCommentThread={openCommentThread}
            monitorCurrentRun={staleEstimate}
            recoveryAllowed={staleEstimate && !legacyView}
            estimateRunning={!!pipeline?.running?.estimate || pipelineResume.busy}
            pickingComment={canWriteComments && pickingCommentBlock}
            jumpAnchor={jumpAnchor}
            commentsOpen={commentsOpen || historyMenuOpen}
          />
        ) : null}

        {/* Explicit Pricing access. Only its assigned approver can advance the pipeline. */}
        {activeTab === 'pricing' && canPricing && legacyView ? <SavedPricingHistory projectId={projectId} variant="tma" /> : null}
        {activeTab === 'pricing' && canPricing && !legacyView ? (
          <PricingTab
            projectId={projectId}
            active
            onPipelineChange={load}
            onDraftStateChange={setPricingDraft}
            onCommentAnchor={canWriteComments ? openCommentAnchor : undefined}
            commentPins={commentPins}
            onOpenCommentThread={openCommentThread}
            pickingComment={canWriteComments && pickingCommentBlock}
            jumpAnchor={jumpAnchor}
            commentsOpen={commentsOpen || historyMenuOpen}
          />
        ) : null}

        {activeTab === 'proposal' && pAccess.canSee ? (
          <ProposalTab
            key={`${projectId}:${proposalNonce}`}
            projectId={projectId}
            readOnly={legacyView}
            architectureApproved={architectureApproved}
            estimateApproved={pipeline?.estimate === 'approved'}
            pricingApproved={pipeline?.pricing === 'approved'}
            proposalRunning={!!pipeline?.running?.proposal}
            onPipelineChange={load}
            onWorkflowRecovered={user?.isRoot ? async () => {
              setRecoveringWorkflow(true);
              try {
                const current = await architectApi.getProject(projectId);
                setProject(current.project); setSpec(current.spec ?? null);
                setSpecFingerprint(current.spec_fingerprint ?? null); setPipeline(current.pipeline);
                setVersions(current.versions || []); setFiles(current.files || []);
                setLoadError(null); setCommentsOpen(false); setProposalNonce(value => value + 1); setTab('proposal');
              } finally { setRecoveringWorkflow(false); }
            } : undefined}
            onCommentAnchor={!legacyView && canWriteComments ? openCommentAnchor : undefined}
            commentPins={commentPins}
            onOpenCommentThread={openCommentThread}
            pickingComment={canWriteComments && pickingCommentBlock}
            jumpAnchor={jumpAnchor}
            commentsOpen={commentsOpen || historyMenuOpen}
          />
        ) : null}
      </div>

      <Sheet open={historyMenuOpen} onClose={() => setHistoryMenuOpen(false)} title="Document history">
        {hasLegacyDocument('estimate') ? <button type="button" className="tma-doc-btn tma-doc-btn-block" onClick={() => openLegacyHistory('estimate')}>Earlier estimates</button> : null}
        {hasLegacyDocument('pricing') ? <button type="button" className="tma-doc-btn tma-doc-btn-block" onClick={() => openLegacyHistory('pricing')}>Saved pricing</button> : null}
        {hasLegacyDocument('proposal') ? <button type="button" className="tma-doc-btn tma-doc-btn-block" onClick={() => openLegacyHistory('proposal')}>Earlier proposals</button> : null}
      </Sheet>

      {/* Rename project — the Save stays inside the sheet. */}
      <Sheet
        open={renameOpen}
        onClose={() => setRenameOpen(false)}
        title="Rename project"
        headerAction={
          <button
            type="button"
            className="tma-sheet-text-btn"
            onClick={() => setRenameOpen(false)}
          >
            Cancel
          </button>
        }
        footer={<button type="button" className="tma-btn tma-btn-primary tma-btn-block" disabled={!renameCanSave || renaming} onClick={() => void doRename()}>{renaming ? 'Saving…' : 'Save name'}</button>}
      >
        {renameError ? (
          <div className="tma-banner err" role="alert">
            {renameError}
          </div>
        ) : null}
        <label className="tma-field-label" htmlFor="tma-rename-input">
          Name
        </label>
        <input
          id="tma-rename-input"
          className="tma-input"
          type="text"
          autoFocus
          maxLength={200}
          placeholder="Project name"
          value={renameValue}
          disabled={renaming}
          onChange={(e) => setRenameValue(e.target.value)}
          onFocus={(e) => e.currentTarget.select()}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void doRename();
            }
          }}
        />
        <p className="tma-field-hint">
          Updates the name everywhere — the poster and every estimate and
          proposal document. Nothing else changes.
        </p>
      </Sheet>
    </div>
  );
}
